import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { Subject, debounceTime, fromEvent, tap } from 'rxjs';
import { Feed, Post, User } from '../../models/core.models';

import { FeedPostViewMode } from 'src/app/api/services/feed-api.service';
import { PostCardComponent } from '../cards/post-card/post-card.component';
import { PropertyViewConfig } from '../../services/feed-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type PostDetailsStatus = {
  [postId: string]: boolean;
};

@Component({
  selector: 'app-post-list',
  templateUrl: './post-list.component.html',
  styleUrls: ['./post-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostListComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static readonly SKELETON_CARD_HEIGHT = 171;
  private static readonly SKELETON_GALLERY_CARD_HEIGHT = 386;
  private static readonly SKELETON_CARD_GAP = 12;
  private static readonly POSTS_BLOCK_PADDING = 8;
  private static readonly SKELETON_FALLBACK_COUNT = 6;

  @ViewChild('masonryContainer') set container(content: ElementRef) {
    if (content && content !== this._container) {
      this._container = content;
      this.setupResizeObserver();

      this.scheduleSkeletonCountUpdate();
    }
  }
  get container(): ElementRef {
    return this._container;
  }
  private _container: ElementRef;
  @ViewChild('cardContainer') cardContainer: ElementRef;
  @ViewChildren('masonryItems') items: QueryList<ElementRef>;
  @ViewChildren('postCardCmp') postCardCmps!: QueryList<PostCardComponent>;

  // Data inputs
  @Input() posts: Post[] = [];
  @Input() loading = false;
  @Input() feed: Feed;
  @Input() viewMode: FeedPostViewMode = 'default';
  @Input() viewConfig: PropertyViewConfig[];
  @Input() timeShown: string;
  @Input() user: User;
  @Input() currentLanguage: string;
  @Input() selectedPostId: string;
  @Input() feedSearchQuery: string;
  @Input() showAdvancedSearch = true;

  // Toolbar — consumers project their own via <ng-content select="[postListToolbar]">.
  /** When false, hides the pagination bar. Defaults to true. */
  @Input() showPagination = true;

  // Post card options
  /** Disables post tools (share, export, etc.) on each card */
  @Input() disableTools = false;
  /** When true, renders post cards in story mode (simplified layout) */
  @Input() isStoryMode = false;
  /** When true, the select checkbox is hidden on post cards */
  @Input() selectable: boolean = true;
  /** Factory function returning a toggle handler per post. Used in related articles dialog. */
  @Input() customTogglePostSelect: (post: Post) => () => void;
  /** Callback for custom post removal (e.g. remove from story). */
  @Input() customRemovePost: (post: Post) => void;

  // Pagination
  @Input() currentPage: number;
  @Input() itemsPerPage: number;
  @Input() totalItemsCount: number;

  // Algorithm
  @Input() loadingAlgorithmPostIds: string[] = [];
  @Input() appliedAlgorithmPostIds: string[] = [];

  // Outputs
  @Output() togglePostDetails = new EventEmitter<{
    event: { isDetailsView: boolean; postId: string };
    index: number;
  }>();
  @Output() requestToggleThread = new EventEmitter<{ postId: string }>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() itemsPerPageChanged = new EventEmitter<number>();
  @Output() imageLoadedEvent = new EventEmitter<void>();

  // Internal state
  postScrollMode = false;
  currentPostIndex = 0;
  postsListCount = 0;
  postDetailsList: PostDetailsStatus = {};
  masonaryLayoutInitialized = false;
  disableScrollHandler = false;
  previousScrollTop = 0;
  scrollTimeout: any;

  skeletonCount = PostListComponent.SKELETON_FALLBACK_COUNT;

  private imageLoadedSubject = new Subject<void>();
  private resizeObserver: ResizeObserver;
  private resizeSubject = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['posts']) {
      this.postsListCount = this.posts?.length ?? 0;
    }

    if (changes['loading'] || changes['viewMode']) {
      if (this.loading === false && this.viewMode === 'gallery') {
        setTimeout(() => this.layoutItems());
      }
      if (this.loading) {
        this.masonaryLayoutInitialized = false;
      }

      if (this.loading || changes['viewMode']) {
        this.updateSkeletonCount();
      }
    }
  }

  ngAfterViewInit(): void {
    this.imageLoadedSubject
      .pipe(
        debounceTime(300),
        tap(() => this.layoutItems()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    if (this.cardContainer) {
      fromEvent(this.cardContainer.nativeElement, 'scroll')
        .pipe(
          debounceTime(50),
          tap(() => this.onScroll()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.setupResizeObserver();

    this.scheduleSkeletonCountUpdate();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  postTrackByFn(index: number, post: Post): string {
    return post.id;
  }

  onTogglePostDetails(
    $event: { isDetailsView: boolean; postId: string },
    index: number,
  ) {
    this.togglePostDetails.emit({ event: $event, index });
    this.scrollToPost(index);
  }

  onRequestToggleThread(event: { postId: string }) {
    this.requestToggleThread.emit(event);
  }

  onPageChanged(page: number) {
    this.pageChanged.emit(page);
  }

  onItemsPerPageChanged(value: number) {
    this.itemsPerPageChanged.emit(value);
  }

  onImageLoaded() {
    this.imageLoadedSubject.next();
    this.imageLoadedEvent.emit();
  }

  // -- Masonry layout --

  layoutItems() {
    if (!this._container?.nativeElement) {
      return;
    }
    const gutterSize = 10;
    const columns = 2;
    const containerWidth = this._container.nativeElement.offsetWidth;
    const horizontalMargins = 2 * gutterSize;
    const totalGutterSpace = columns * gutterSize;
    const availableWidth = containerWidth - horizontalMargins;
    const itemWidth = (availableWidth - totalGutterSpace) / columns;

    let columnHeights = Array(columns).fill(0);
    const openItemHeight = 800;

    if (this.items?.length > 0) {
      this.items.forEach((item: ElementRef) => {
        if (item.nativeElement.classList.contains('details')) {
          let maxHeight = Math.max(...columnHeights);
          let containerWidthBig = containerWidth - 2 * gutterSize;
          item.nativeElement.style.width = `${containerWidthBig}px`;
          item.nativeElement.style.height = `${openItemHeight}px`;
          item.nativeElement.style.transform = `translate(0px, ${maxHeight}px)`;
          item.nativeElement.style.position = `absolute`;
          columnHeights.forEach((_, idx) => {
            columnHeights[idx] += openItemHeight + gutterSize;
          });
          maxHeight = Math.max(...columnHeights);
          columnHeights = columnHeights.map(() => maxHeight);
        } else {
          const minColumnHeight = Math.min(...columnHeights);
          const index = columnHeights.indexOf(minColumnHeight);
          let x = index * (itemWidth + gutterSize);
          let y = minColumnHeight;
          item.nativeElement.style.transform = `translate(${x}px, ${y}px)`;
          item.nativeElement.style.width = `${itemWidth}px`;
          item.nativeElement.style.position = `absolute`;
          columnHeights[index] += item.nativeElement.offsetHeight + gutterSize;
        }
      });
    }

    setTimeout(() => {
      this.masonaryLayoutInitialized = true;
    });
  }

  // -- Loading placeholders --
  private scheduleSkeletonCountUpdate(): void {
    setTimeout(() => this.updateSkeletonCount());
  }

  /**
   * Fits the placeholder list to the visible container: the largest N with
   * `N * height + (N - 1) * gap <= usable`. Floor rather than ceil - a partially
   * visible last card would overflow, which is the whole thing we are avoiding.
   */
  private updateSkeletonCount(): void {
    const isGallery = this.viewMode === 'gallery';
    const element = isGallery
      ? this._container?.nativeElement
      : this.cardContainer?.nativeElement;

    if (!element) {
      return;
    }

    const cardHeight = isGallery
      ? PostListComponent.SKELETON_GALLERY_CARD_HEIGHT
      : PostListComponent.SKELETON_CARD_HEIGHT;
    const gap = PostListComponent.SKELETON_CARD_GAP;
    const usable =
      element.clientHeight -
      (isGallery ? 0 : PostListComponent.POSTS_BLOCK_PADDING);

    this.skeletonCount = Math.max(
      1,
      Math.floor((usable + gap) / (cardHeight + gap)),
    );
  }

  // -- Scroll / post navigation --

  scrollToPost(index: number): void {
    if (!this.cardContainer) return;
    const postElement = this.cardContainer.nativeElement.querySelector(
      `#post-${index}`,
    );
    if (postElement) {
      this.disableScrollHandler = true;
      this.cardContainer.nativeElement.scrollTo({
        top: postElement.offsetTop - 10,
        behavior: 'smooth',
      });
      setTimeout(() => {
        this.disableScrollHandler = false;
      }, 500);
    }
  }

  private onScroll(): void {
    if (this.postScrollMode) {
      if (this.disableScrollHandler) {
        return;
      }
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.handleScrollDirection();
      }, 100);
    }
  }

  private snapToClosestPost(): void {
    const container = this.cardContainer.nativeElement;
    const children = container.children;
    let closestIndex = 0;
    let minDistance = Infinity;

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const rect = child.getBoundingClientRect();
      const distance = Math.abs(
        rect.top - container.getBoundingClientRect().top,
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    this.currentPostIndex = closestIndex;
  }

  private handleScrollDirection(): void {
    const currentScrollTop = this.cardContainer.nativeElement.scrollTop;
    this.snapToClosestPost();
    if (currentScrollTop > this.previousScrollTop) {
      this.onNextPost();
    } else {
      this.onPreviousPost();
    }
    this.previousScrollTop = currentScrollTop;
  }

  private onNextPost(): void {
    if (this.currentPostIndex < this.postsListCount - 1) {
      this.currentPostIndex++;
      this.scrollToPost(this.currentPostIndex);
    }
  }

  private onPreviousPost(): void {
    if (this.currentPostIndex > 0) {
      this.currentPostIndex--;
      this.scrollToPost(this.currentPostIndex);
    }
  }

  private setupResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (!this._container?.nativeElement) {
      return;
    }
    this.resizeSubject
      .pipe(
        debounceTime(300),
        tap(() => {
          if (this.viewMode === 'gallery') {
            this.layoutItems();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeSubject.next();
    });
    this.resizeObserver.observe(this._container.nativeElement);
  }
}
